import { WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { formatShortDate, formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  List,
  ListItem,
  ListItemText,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { addDays, setHours, setMinutes, setSeconds, startOfDay } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { WORKPLACE_DOCUMENTS_FULL_PATH } from "../../Documents/paths";
import { type PlacementDetail } from "../api/useGetPlacement";
import { PLACEMENT_PATHS } from "../paths";
import { getMissingDocuments } from "../utils/getMissingDocuments";

interface InterviewDocumentReminderBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  selectedInterviewTime: { start: Date; end: Date };
}

export function InterviewDocumentReminderBottomSheet(
  props: InterviewDocumentReminderBottomSheetProps
) {
  const { modalState, placementDetails, selectedInterviewTime } = props;
  const history = useHistory();

  // Document deadline is 4 PM the day before the interview
  const dayBeforeInterview = addDays(startOfDay(selectedInterviewTime.start), -1);
  const documentDeadline = setSeconds(setMinutes(setHours(dayBeforeInterview, 16), 0), 0);
  const deadlineDateFormatted = formatShortDate(documentDeadline);

  const interviewTimeFormatted = formatTime(selectedInterviewTime.start);
  const interviewDateFormatted = formatShortDate(selectedInterviewTime.start);

  const missingDocuments = getMissingDocuments(placementDetails);

  const handleUploadNow = () => {
    modalState.closeModal();
    history.push(
      `${generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
        workplaceId: placementDetails.workplace.id,
      })}?domain=${WorkerRequirementDomain.HIRING}`
    );
  };

  const handleRemindMeLater = () => {
    modalState.closeModal();
    history.push(PLACEMENT_PATHS.ROOT);
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleRemindMeLater} />}
      footer={
        <DialogFooter orientation="vertical">
          <Button fullWidth variant="contained" size="large" onClick={handleUploadNow}>
            Upload documents now
          </Button>
          <TextButton size="large" sx={{ my: 3 }} onClick={handleRemindMeLater}>
            Remind me later
          </TextButton>
        </DialogFooter>
      }
      onClose={handleRemindMeLater}
    >
      <Box sx={{ px: 7, py: 6 }}>
        <Stack spacing={6}>
          <Text variant="h4" sx={{ fontWeight: 500 }}>
            Reminder: You must submit the following documents by 4pm on {deadlineDateFormatted} for
            your interview at {interviewTimeFormatted}, {interviewDateFormatted}:
          </Text>

          <List sx={{ listStyleType: "disc", pl: 5, width: "100%" }}>
            {missingDocuments.map((requirement) => (
              <ListItem key={requirement.id} sx={{ display: "list-item", pb: 0, pt: 1 }}>
                <ListItemText>
                  <Text bold>{requirement.relationships?.requirement?.data?.attributes?.name}</Text>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
